<script lang="ts">
import { computed } from 'vue';

import { type Theme, getThemeClass } from '@sg/resources/assets/js/theme';
import { PhxsgPicture } from '@sg/src/View/Components/Atoms/Picture';
import type { PhxPictureProps } from '@sg-core/src/View/Components/Atoms/Picture';
import { PhxsgVisuallyHidden } from '@sg-core/src/View/Components/Atoms/VisuallyHidden';
import {
  PhxsgFocus as PhxsgCoreFocus,
  PhxsgFocusPoint as PhxsgCoreFocusPoint,
  type PhxsgFocusProps as PhxsgCoreFocusProps,
  type PhxsgFocusAction,
} from '@sg-core/src/View/Components/Organisms/Focus';

export interface PhxsgPiaFocusAction extends PhxsgFocusAction {
  href: string;
  title: string;
}

export interface PhxsgFocusProps extends Omit<PhxsgCoreFocusProps, 'actions'> {
  theme?: Theme;
  background: PhxPictureProps;
  actions: PhxsgPiaFocusAction[];
}
</script>

<script setup lang="ts">
const {
  theme = 'blue-80',
  background,
  actions,
  breakpoints = ['', 'xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl'],
} = defineProps<PhxsgFocusProps>();

defineEmits<{
  'focus:usp-open': [payload: { tracking?: Record<string, unknown> }];
}>();

const themeClass = computed(() => getThemeClass(theme));
</script>

<template>
  <PhxsgCoreFocus
    :actions="actions"
    :breakpoints="breakpoints"
    :class="themeClass"
    @focus:usp-open="$emit('focus:usp-open', $event)"
  >
    <template #background>
      <PhxsgPicture v-bind="background" class="phxsg-focus__background-image" />
    </template>

    <template #usp="{ usp, position }">
      <PhxsgCoreFocusPoint v-if="position" use="a" :href="usp.href" :position="position" pulse animate>
        <PhxsgVisuallyHidden>
          {{ usp.title }}
        </PhxsgVisuallyHidden>
      </PhxsgCoreFocusPoint>
    </template>
  </PhxsgCoreFocus>
</template>
